import { FC, useCallback, useEffect, useRef } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Dropdown, InlineNotification } from '@carbon/react';
import {
  CheckmarkFilled,
  CircleDash,
  InProgress,
  Misuse,
} from '@carbon/react/icons';
import { useAtom } from 'jotai';
import classNames from 'classnames';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { PlainLink } from 'components/link';
import { formatNumber } from 'components/search/common/format-helpers';
import { StatsBar } from 'components/stats-bar';
import { usePolling } from 'lib/hooks/usePolling';
import {
  hoursAtom,
  statusFilterAtom,
  triggerTypeFilterAtom,
  textAtom,
  sortAtom,
  columnFiltersAtom,
  pageAtom,
  pageSizeAtom,
  wellSpacingJobStatusAtom,
  fetchJobStatus,
  buildFingerprint,
} from './atoms';
import { WellSpacingsSubTable } from './well-spacings-table';
import { WorkflowStatusCell, ValidationStatusCell } from './status-cells';
import {
  POLLING_INTERVAL_MS,
  LOOKBACK_OPTIONS,
  STATUS_OPTIONS,
  TRIGGER_TYPE_OPTIONS,
  TRIGGER_TYPE_LABELS,
  formatDuration,
} from './utils';
import type {
  WellSpacingJob,
  WorkflowStatus,
  ValidationStatus,
  LookbackOption,
  FilterOption,
} from './types';
import styles from './page.module.scss';

// --- Column definitions ---

const baseColumns: ColumnDef<WellSpacingJob, any>[] = [
  {
    accessorKey: 'jobId',
    id: 'jobId',
    header: 'Job ID',
    size: 130,
    // Searchable via the global search box (wsj- ids), not a header filter.
    enableColumnFilter: false,
    cell: (info) => <span className={styles.mono}>{info.getValue()}</span>,
  },
  {
    accessorKey: 'wellName',
    id: 'wellName',
    header: 'Well Name',
    size: 180,
    meta: { type: 'text' },
    // Filterable server-side; not sortable (would require a survey-geometry join).
    enableSorting: false,
    cell: (info) => {
      const name = info.getValue() as string | null;
      const href = info.row.original.wellHref;
      if (!name) return '\u2014';
      return href ? (
        <PlainLink href={href} className={styles.wellLink}>
          {name}
        </PlainLink>
      ) : (
        name
      );
    },
  },
  {
    accessorKey: 'triggerWellboreUwi14',
    id: 'triggerWellboreUwi14',
    header: 'Trigger Wellbore (UWI14)',
    size: 230,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue() as string | null;
      const href = info.row.original.wellHref;
      if (!value) return '\u2014';
      return href ? (
        <PlainLink href={href} className={styles.mono}>
          {value}
        </PlainLink>
      ) : (
        <span className={styles.mono}>{value}</span>
      );
    },
  },
  {
    accessorKey: 'triggerType',
    id: 'triggerType',
    header: 'Trigger Type',
    size: 130,
    // Filtered via the toolbar "Trigger Type" dropdown, not a header filter.
    enableColumnFilter: false,
    cell: (info) => {
      const value = info.getValue() as string | null;
      return value ? TRIGGER_TYPE_LABELS[value] || value : '\u2014';
    },
  },
  {
    accessorKey: 'workflowStatus',
    id: 'workflowStatus',
    header: 'Status',
    size: 140,
    // Filtered via the toolbar "Status" dropdown, not a header filter.
    enableColumnFilter: false,
    cell: (info) => (
      <WorkflowStatusCell
        status={info.getValue() as WorkflowStatus}
        failureReason={info.row.original.failureReason}
      />
    ),
  },
  {
    accessorKey: 'validationStatus',
    id: 'validationStatus',
    header: 'Geom. Status',
    size: 140,
    // Derived post-pagination from child counts — not server-filterable/sortable.
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => (
      <ValidationStatusCell status={info.getValue() as ValidationStatus} />
    ),
  },
  {
    accessorKey: 'searchAreaFt',
    id: 'searchAreaFt',
    header: 'Search Area',
    size: 180,
    // `type: 'decimal'` drives the range filter and comma-formats the cell;
    // export the raw number so it lands in a spreadsheet as a number, not text.
    meta: {
      type: 'decimal',
      align: 'left',
      getExportValue: (row: WellSpacingJob) => row.searchAreaFt,
    },
    cell: (info) => {
      const expanded = info.row.original.isSearchAreaExpanded;
      const text = `${formatNumber(info.getValue() as number)} ft${
        expanded ? ' (expanded)' : ''
      }`;
      return expanded ? (
        <span className={classNames(styles.searchAreaExpanded)}>{text}</span>
      ) : (
        text
      );
    },
  },
  {
    accessorKey: 'offsetWellsCount',
    id: 'offsetWellsCount',
    header: 'Offset Wells',
    size: 130,
    meta: { type: 'integer', align: 'left' },
    // Aggregated post-pagination — not server-filterable/sortable.
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    accessorKey: 'durationSeconds',
    id: 'durationSeconds',
    header: 'Duration',
    size: 100,
    // Sortable, but no range filter (values are version-derived, not a column).
    enableColumnFilter: false,
    cell: (info) => formatDuration(info.getValue() as number | null),
  },
  {
    accessorKey: 'createdAt',
    id: 'createdAt',
    header: 'Created At',
    size: 190,
    meta: { type: 'datetime' },
    // Windowed by the toolbar lookback dropdown, not a header filter.
    enableColumnFilter: false,
  },
  {
    accessorKey: 'completedAt',
    id: 'completedAt',
    header: 'Completed At',
    size: 190,
    meta: { type: 'datetime' },
    enableColumnFilter: false,
  },
];

// --- Page component ---

export const WellSpacingJobStatusPage: FC = () => {
  const [hours, setHours] = useAtom(hoursAtom);
  const [statusFilter, setStatusFilter] = useAtom(statusFilterAtom);
  const [triggerTypeFilter, setTriggerTypeFilter] = useAtom(
    triggerTypeFilterAtom
  );
  const [text, setText] = useAtom(textAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtom);
  const [page, setPage] = useAtom(pageAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);
  const [loadableState, refetch] = useAtom(wellSpacingJobStatusAtom);
  const fingerprintRef = useRef<string>('');

  const isLoading = loadableState.state === 'loading';
  const error =
    loadableState.state === 'hasError'
      ? loadableState.error instanceof Error
        ? loadableState.error.message
        : 'Failed to fetch data'
      : null;

  const data =
    loadableState.state === 'hasData'
      ? (loadableState.data?.results ?? [])
      : [];
  const stats =
    loadableState.state === 'hasData' ? loadableState.data?.stats : null;
  const totalCount =
    loadableState.state === 'hasData'
      ? (loadableState.data?.totalCount ?? 0)
      : 0;

  // Keep fingerprint in sync with current data
  useEffect(() => {
    if (loadableState.state === 'hasData' && loadableState.data) {
      fingerprintRef.current = buildFingerprint(loadableState.data);
    }
  }, [loadableState]);

  const hasActiveJobs = data.some(
    (job) =>
      job.workflowStatus === 'in_progress' ||
      job.workflowStatus === 'not_started'
  );

  // Poll in the background while active jobs exist.
  // Only trigger a state refetch when the fingerprint actually changes
  // so the table doesn't flicker on unchanged payloads.
  const pollFn = useCallback(async () => {
    const fresh = await fetchJobStatus({
      hours,
      statusFilter,
      triggerTypeFilter,
      text,
      sort,
      columnFilters,
      page,
      pageSize,
    });
    const newFingerprint = buildFingerprint(fresh);
    if (newFingerprint !== fingerprintRef.current) {
      refetch();
    }
  }, [
    hours,
    statusFilter,
    triggerTypeFilter,
    text,
    sort,
    columnFilters,
    page,
    pageSize,
    refetch,
  ]);

  usePolling({
    fn: pollFn,
    intervalMs: POLLING_INTERVAL_MS,
    enabled: hasActiveJobs,
    immediate: false,
  });

  const activeJobs = data.filter((job) => job.workflowStatus === 'in_progress');
  const activeJobCount = activeJobs.length;
  const exampleActiveWell =
    activeJobs[0]?.wellName || activeJobs[0]?.triggerWellboreUwi14;

  const handleHoursChange = ({
    selectedItem,
  }: {
    selectedItem: LookbackOption | null;
  }) => {
    if (selectedItem) {
      setHours(parseInt(selectedItem.id, 10));
      setPage(1);
    }
  };

  const handleStatusFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setStatusFilter(selectedItem.id);
      setPage(1);
    }
  };

  const handleTriggerTypeFilterChange = ({
    selectedItem,
  }: {
    selectedItem: FilterOption | null;
  }) => {
    if (selectedItem) {
      setTriggerTypeFilter(selectedItem.id);
      setPage(1);
    }
  };

  const handleLoadData = useCallback(
    ({
      page: newPage,
      pageSize: newPageSize,
      sorting,
      columnFilters: newColumnFilters,
      globalFilter,
    }: TableState) => {
      setPage(newPage);
      setPageSize(newPageSize);
      setSort(
        sorting.length > 0 ? { id: sorting[0].id, desc: sorting[0].desc } : null
      );
      setColumnFilters(
        newColumnFilters.map(({ id, value }) => ({ id, value }))
      );
      setText(globalFilter ?? '');
    },
    [setPage, setPageSize, setSort, setColumnFilters, setText]
  );

  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title="Well Spacing Job Status"
        actions={null}
      />
      <p className={styles.subtitle}>
        Automatic well spacing jobs triggered by new wellbore geometries and
        geometry updates
      </p>

      {activeJobCount > 0 && (
        <InlineNotification
          kind="info"
          title={`${formatNumber(activeJobCount)} job${
            activeJobCount > 1 ? 's' : ''
          } currently running`}
          subtitle={`Processing: ${exampleActiveWell || ''}${
            activeJobCount > 1
              ? ` and ${formatNumber(activeJobCount - 1)} other${
                  activeJobCount > 1 ? 's' : ''
                }`
              : ''
          }`}
          lowContrast
          hideCloseButton
          className={styles.notification}
        />
      )}

      {stats && (
        <StatsBar
          format="value-first"
          stats={[
            { label: 'Total', value: stats.total, tagType: 'gray' },
            {
              label: 'Completed',
              value: stats.complete,
              tagType: 'green',
              renderIcon: CheckmarkFilled,
            },
            {
              label: 'Running',
              value: stats.inProgress,
              tagType: 'blue',
              renderIcon: InProgress,
            },
            {
              label: 'Pending',
              value: stats.notStarted,
              tagType: 'gray',
              renderIcon: CircleDash,
            },
            {
              label: 'Failed',
              value: stats.failed,
              tagType: 'red',
              renderIcon: Misuse,
            },
          ]}
        />
      )}

      <DataTable
        key={`${hours}-${statusFilter}-${triggerTypeFilter}`}
        data={data}
        columns={baseColumns}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        error={error}
        persistenceKey="well-spacing-job-status"
        initialTableState={{
          sortColumn: sort?.id ?? 'createdAt',
          sortDirection: sort ? (sort.desc ? 'desc' : 'asc') : 'desc',
          columnFilters,
          globalFilter: text,
          pageSize,
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount,
        }}
        onPaginationChange={handleLoadData}
        onSortChange={handleLoadData}
        onFilterChange={handleLoadData}
        onGlobalSearchChange={handleLoadData}
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'well-spacing-job-status',
              },
            ],
          },
        }}
        searchPlaceholder="Search well name, UWI14, or Job ID"
        rightToolbarActions={[
          <Dropdown
            key="status"
            id="status-filter-dropdown"
            titleText=""
            label="All Statuses"
            size="sm"
            items={STATUS_OPTIONS}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              STATUS_OPTIONS.find((opt) => opt.id === statusFilter) ??
              STATUS_OPTIONS[0]
            }
            onChange={handleStatusFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="trigger"
            id="trigger-type-filter-dropdown"
            titleText=""
            label="All Trigger Types"
            size="sm"
            items={TRIGGER_TYPE_OPTIONS}
            itemToString={(item: FilterOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={
              TRIGGER_TYPE_OPTIONS.find(
                (opt) => opt.id === triggerTypeFilter
              ) ?? TRIGGER_TYPE_OPTIONS[0]
            }
            onChange={handleTriggerTypeFilterChange}
            className={styles.toolbarDropdown}
          />,
          <Dropdown
            key="hours"
            id="lookback-dropdown"
            titleText=""
            label="Last 24 hours"
            size="sm"
            items={LOOKBACK_OPTIONS}
            itemToString={(item: LookbackOption | null) => {
              return item ? item.label : '';
            }}
            selectedItem={LOOKBACK_OPTIONS.find(
              (opt) => opt.id === String(hours)
            )}
            onChange={handleHoursChange}
            className={styles.toolbarDropdown}
          />,
        ]}
        renderRowSubComponent={(row: Row<WellSpacingJob>) => {
          const job = row.original;
          return <WellSpacingsSubTable requestId={job.id} />;
        }}
      />
    </div>
  );
};
